import { getTokenIfPresent, setToken } from '@luminovo/auth';
import type { QueryClient } from '@tanstack/react-query';

import { ensureSession, type Session } from '@/shared/permissions/session';

/** `context.auth`: `_authenticated`'s `beforeLoad` waits on it before page chunks load, then sets `context.session`. */
export interface RouterAuth {
    /** Whether there is a session; on a cold load, once Auth0 has finished loading and handed out a token. */
    isSignedIn(): Promise<boolean>;

    /**
     * Sets the API token unless the app holds one, then `ensureSession` (which skips the language for a `preload`).
     * Only once `isSignedIn()` has resolved `true`.
     */
    loadSession(queryClient: QueryClient, options: { preload: boolean }): Promise<Session>;
}

/** What the Auth0 SDK knows once it has finished loading, from `useAuth`. */
export interface Auth0Session {
    isAuthenticated: boolean;
    getAccessTokenSilently(): Promise<string>;
}

/** The {@link RouterAuth} `App.tsx` creates, with the `report` that keeps it up to date. */
export interface ReportedRouterAuth extends RouterAuth {
    /** Called with the SDK's state once it has finished loading, and again whenever it changes. */
    report(session: Auth0Session): void;
}

// No component lives here, so editing `TenantAndAuthProviders` hot-reloads that component alone.
/** Hands the SDK's session, kept in React state a `beforeLoad` can't read, to the router as `context.auth`. */
export function createRouterAuth(): ReportedRouterAuth {
    let reportFirst: (session: Auth0Session) => void = () => {};
    let reported = new Promise<Auth0Session>((resolve) => {
        reportFirst = resolve;
    });
    return {
        isSignedIn: async () => {
            const { isAuthenticated, getAccessTokenSilently } = await reported;
            // Only once the app holds a token: the SDK's localStorage cache (all but production) keeps the user of a
            // session Auth0 refuses to renew, as it does with `consent_required` on localhost.
            if (isAuthenticated && getTokenIfPresent() !== undefined) {
                return true;
            }
            // Not necessarily signed out: the SDK skips Auth0 once its day-long `is.authenticated` cookie lapses.
            return getAccessTokenSilently().then(
                () => true,
                () => false,
            );
        },
        loadSession: async (queryClient, options) => {
            // Once per page load: then `AuthenticatedContextProvider` holds and renews the token, and reloads the app
            // when a renewal brings another user's. A token set here would go around that check.
            if (getTokenIfPresent() === undefined) {
                const { getAccessTokenSilently } = await reported;
                setToken(await getAccessTokenSilently());
            }
            return ensureSession(queryClient, options);
        },
        report: (session) => {
            // Settles the loads that have been waiting since the cold load; a no-op once settled.
            reportFirst(session);
            reported = Promise.resolve(session);
        },
    };
}
